import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import type { EPCR, Incident, FireReport } from '@/types';
import { computeNerisReadiness } from '@/lib/neris-readiness';
import { useActiveEpcrStore } from '@/lib/active-epcr-store';
import { useThemeStore } from '@/lib/theme-store';
import { SectionCard, type SectionCardStatus } from '@/components/cockpit';
import { cn } from '@/lib/cn';
import { PatientDataTab } from './tabs/PatientDataTab';
import { MedicalHistoryTab } from './tabs/MedicalHistoryTab';
// TraumaTab is no longer rendered as a top-level tab. The pipeline
// design says conditional content unlocks inline at the stage that
// triggers it — TraumaTab now appears as a card inside AssessmentTab
// when the cascade engine flags trauma as active.
import { DispatchSceneTab } from './tabs/DispatchSceneTab';
import { AssessmentTab } from './tabs/AssessmentTab';
import { VitalsTab } from './tabs/VitalsTab';
import { ProceduresTab } from './tabs/ProceduresTab';
import { MedicationsTab } from './tabs/MedicationsTab';
import { DispositionTab } from './tabs/DispositionTab';
import { NarrativeTab } from './tabs/NarrativeTab';
import { TreatmentSummaryTab } from './tabs/TreatmentSummaryTab';
import { RequiredDocTab } from './tabs/RequiredDocTab';
import { TransferOfCareTab } from './tabs/TransferOfCareTab';
import { AttachmentsTab } from './tabs/AttachmentsTab';
import { AmendmentsTab } from './tabs/AmendmentsTab';
import { BillingTab } from './tabs/BillingTab';
// ProtocolPanel + the iframe protocol-reference drawer were a right-
// column embed that ate ~440px of horizontal space, was redundantly
// labeled, and never finished its wiring. Per Cameron's call: rip
// the whole right column out, let the input fields fill the width,
// and let the protocol-driven reporting live where it belongs —
// inside the Procedures tab as the CareMatrix.
import { useUILayoutStore, type EpcrLayoutMode } from '@/lib/ui-layout-store';
import {
  LeftRailWorkflow, GridLauncherWorkflow, CommandRailWorkflow,
  type WorkflowSection, type SectionStatus,
} from './WorkflowLayouts';
import {
  ArrowLeft, User, Radio, Stethoscope, Activity,
  Syringe, Pill, Truck, FileText, Lock, Loader2, BookOpen, ClipboardCheck, Printer, ClipboardList,
  Users as UsersIcon, Paperclip, FilePen, ShieldCheck,
  LayoutDashboard, LayoutGrid, PanelRight, HeartPulse, DollarSign, Flame,
} from 'lucide-react';
import { Code99Mode } from '@/modules/code99/Code99Mode';
import { TimestampRail } from '@/components/TimestampRail';
import { StickyDataImportBar } from '@/components/StickyDataImportBar';
import { ConditionalSetBanner } from '@/components/ConditionalSetBanner';
import { PostValidationModal } from '@/components/PostValidationModal';
import { CascadeRemovalPrompt } from '@/components/CascadeRemovalPrompt';
// ResponseDelaysPanel is now rendered inside DispositionTab — moved
// out of the top of EPCRPage so the page header isn't competing with
// an empty 'No delays reported' bar on every visit.
import { NemsisPreviewModal } from '@/components/NemsisPreviewModal';
import { isSetActive } from '@/lib/conditional-cascade';
import { useSwipe } from '@/lib/use-swipe';

// Sections aligned to the EMSPCR Data Pipeline (see lib/pipeline-graph.ts).
// Order follows the canonical flow a user moves through: Dispatch →
// Patient → History → Assessment (with conditional trauma/sets cards
// inline) → Vitals → Procedures → Meds → Disposition → Handoff →
// Attachments → Narrative → Summary → Billing → Required → Amendments.
//
// Trauma was removed from this list per the pipeline design — its content
// renders as an inline card inside Assessment when the cascade engine
// activates it. Keeping it here as a static slot would have asked the
// crew to navigate to a tab that's irrelevant 80% of the time.
const SECTIONS: WorkflowSection[] = [
  { key: 'dispatch',     label: 'Dispatch',     icon: Radio,          phase: 'Intake'  },
  { key: 'patient',      label: 'Patient',      icon: User,           phase: 'Intake'  },
  { key: 'history',      label: 'History',      icon: BookOpen,       phase: 'Intake'  },
  { key: 'assessment',   label: 'Assessment',   icon: Stethoscope,    phase: 'Care'    },
  { key: 'vitals',       label: 'Vitals',       icon: Activity,       phase: 'Care'    },
  { key: 'procedures',   label: 'Procedures',   icon: Syringe,        phase: 'Care'    },
  { key: 'medications',  label: 'Meds',         icon: Pill,           phase: 'Care'    },
  { key: 'disposition',  label: 'Disposition',  icon: Truck,          phase: 'Handoff' },
  { key: 'toc',          label: 'Handoff',      icon: UsersIcon,      phase: 'Handoff' },
  { key: 'attachments',  label: 'Attachments',  icon: Paperclip,      phase: 'Handoff' },
  { key: 'narrative',    label: 'Narrative',    icon: FileText,       phase: 'Admin'   },
  { key: 'summary',      label: 'Summary',      icon: ClipboardCheck, phase: 'Admin'   },
  { key: 'billing',      label: 'Billing',      icon: DollarSign,     phase: 'Admin'   },
  { key: 'required',     label: 'Required Doc', icon: ClipboardList,  phase: 'Admin'   },
  { key: 'amendments',   label: 'Amendments',   icon: FilePen,        phase: 'Admin'   },
];

const tabs = SECTIONS.map(({ key, label, icon }) => ({ key, label, icon })) as readonly {
  key: string; label: string; icon: typeof User;
}[];

type TabKey =
  | 'patient' | 'history' | 'dispatch' | 'required'
  | 'assessment' | 'vitals'
  | 'procedures' | 'medications' | 'disposition' | 'attachments'
  | 'toc' | 'summary' | 'narrative' | 'billing' | 'amendments';

// Per-section status heuristic. Lightweight so it can run on every
// render; deeper validation stays in finalization-validator.ts.
// Drives the workflow rail's hot-steel-cools-to-metal coloring —
// every tab needs a real signal so the chief sees the rail
// progressively cool as the workflow fills.
function computeSectionStatus(key: TabKey, epcr: EPCR): SectionStatus {
  switch (key) {
    case 'patient': {
      const has = !!epcr.patient_first_name && !!epcr.patient_last_name;
      return !has ? 'pending' : epcr.patient_dob && epcr.patient_gender ? 'complete' : 'partial';
    }
    case 'history': {
      // Complete when any history is documented OR explicitly cleared
      // (NKA / no meds blanket-no toggles).
      const has =
        (epcr.medical_history?.length ?? 0) > 0 ||
        ((epcr as unknown as { current_medications?: unknown[] }).current_medications?.length ?? 0) > 0 ||
        (epcr.allergies?.length ?? 0) > 0;
      return has ? 'complete' : 'pending';
    }
    case 'dispatch':
      return epcr.chief_complaint && epcr.primary_impression ? 'complete' :
             epcr.chief_complaint || epcr.primary_impression ? 'partial' : 'pending';
    case 'required':
      // Required-doc tab is satisfied when chief complaint + impression
      // + transport decision are all present (the three protocol-
      // mandated documentation pillars).
      return epcr.chief_complaint && epcr.primary_impression && epcr.transport_decision
        ? 'complete'
        : (epcr.chief_complaint || epcr.primary_impression) ? 'partial' : 'pending';
    case 'vitals': {
      // Vitals live on the vital_sets table — can't read from here
      // synchronously. Proxy via transport_decision (set after the
      // run progressed enough that vitals were taken). Keeps the rail
      // signaling "still red until the run progresses past patient
      // contact."
      return epcr.transport_decision ? 'complete' : 'pending';
    }
    case 'procedures':
    case 'medications':
      // Both tabs satisfied when a transport decision exists (signals
      // the run is far enough along that procedure/med decisions have
      // been made — empty rows are valid ("none performed")).
      return epcr.transport_decision ? 'complete' : 'pending';
    case 'disposition':
      return epcr.transport_decision ? 'complete' : 'pending';
    case 'toc':
      return epcr.toc_completed ? 'complete'
        : epcr.transport_decision === 'transport_this_unit' ? 'partial'
        : 'pending';
    case 'summary':
    case 'attachments':
      // Read-mostly tabs — never blocking, but mirror dispatch state
      // so the rail doesn't keep them perpetually red after the run.
      return epcr.transport_decision ? 'complete' : 'pending';
    case 'narrative':
      return epcr.narrative_approved ? 'complete' : 'pending';
    case 'billing':
      // Billing satisfied when destination + transport decision are
      // present (the minimum needed for the Comstar export to compile).
      return epcr.transport_decision === 'transport_this_unit' && !!epcr.destination_hospital
        ? 'complete'
        : epcr.transport_decision ? 'partial' : 'pending';
    case 'amendments':
      // Amendments are inherently optional — never block. Mark as
      // complete once the run has progressed past disposition so the
      // rail doesn't stay red forever.
      return epcr.transport_decision ? 'complete' : 'pending';
    case 'assessment': {
      // Assessment status now includes the conditional Trauma Set
      // since trauma is rendered inline here. If trauma isn't
      // triggered, the assessment is complete when there are body-
      // system findings. If trauma IS triggered, we additionally
      // require at least one cause + one triage criterion.
      const baseHas = epcr.assessments.length > 0;
      const traumaActive = isSetActive(epcr, 'trauma');
      if (!traumaActive) return baseHas ? 'complete' : 'pending';
      const causeCount = (epcr.injury_causes ?? []).length;
      const triageCount = (epcr.trauma_triage_red ?? []).length + (epcr.trauma_triage_yellow ?? []).length;
      if (baseHas && causeCount > 0 && triageCount > 0) return 'complete';
      if (baseHas || causeCount > 0 || triageCount > 0) return 'partial';
      return 'pending';
    }
    default:
      return 'pending';
  }
}

export function EPCRPage() {
  const { incidentId, epcrId } = useParams<{ incidentId: string; epcrId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [epcr, setEpcr] = useState<EPCR | null>(null);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('patient');
  const [loading, setLoading] = useState(true);
  // Default protocol-panel visibility is device-dependent: show by default
  const { epcrLayout, setEpcrLayout } = useUILayoutStore();
  const [code99Open, setCode99Open] = useState(false);
  const [postCheckOpen, setPostCheckOpen] = useState(false);
  const [nemsisPreviewOpen, setNemsisPreviewOpen] = useState(false);

  // Foundation §7.2 — left/right swipe between ePCR sections on
  // phone. Ignores inputs/textareas/buttons so form entry isn't
  // hijacked. Tablet / desktop keep pointer-first interaction.
  useSwipe({
    onLeft: () => {
      const idx = SECTIONS.findIndex((s) => s.key === activeTab);
      if (idx >= 0 && idx < SECTIONS.length - 1) setActiveTab(SECTIONS[idx + 1].key as TabKey);
    },
    onRight: () => {
      const idx = SECTIONS.findIndex((s) => s.key === activeTab);
      if (idx > 0) setActiveTab(SECTIONS[idx - 1].key as TabKey);
    },
  });

  useEffect(() => {
    if (!incidentId || !user) return;
    loadOrCreate();
  }, [incidentId, epcrId]);

  // Publish the active EPCR to the global store so the AppShell
  // diamond-plate banner can render Import CAD / Import Vitals/ECG
  // in its top toolbar slot. Clear on unmount so off-EPCR routes
  // don't show those buttons.
  useEffect(() => {
    if (epcr && incidentId) {
      useActiveEpcrStore.getState().setActive(epcr, incidentId);
    }
    return () => useActiveEpcrStore.getState().clear();
  }, [epcr?.id, incidentId]);

  async function loadOrCreate() {
    const inc = await db.incidents.get(incidentId!);
    setIncident(inc ?? null);

    if (epcrId) {
      const existing = await db.epcrs.get(epcrId);
      setEpcr(existing ?? null);
      // HIPAA per-record access log: stamp an 'open' event with
      // server-side IP. Deduped to once-per-30s so React strict-mode
      // double-mounts don't double-log. Sprint J.
      if (existing) {
        // Lazy-import to avoid pulling supabase into the hot render path.
        import('@/lib/epcr-audit').then(({ logEpcrAccess }) => {
          logEpcrAccess(existing.id, 'open');
        });
      }
    } else {
      // Create new ePCR for this incident
      const newEpcr: EPCR = {
        id: crypto.randomUUID(),
        incident_id: incidentId!,
        agency_id: user!.agency_id,
        patient_first_name: null,
        patient_middle_name: null,
        patient_last_name: null,
        patient_suffix: null,
        patient_dob: null,
        patient_gender: null,
        patient_race: null,
        patient_address: null,
        patient_city: null,
        patient_state: null,
        patient_zip: null,
        patient_country: 'US',
        patient_ssn_encrypted: null,
        patient_eye_color: null,
        patient_height_cm: null,
        patient_id_number: null,
        patient_id_state: null,
        patient_id_type: null,
        patient_id_expiration: null,
        patient_id_issue_date: null,
        patient_id_scan_source: null,
        patient_id_scan_timestamp: null,
        patient_id_scan_status: null,
        patient_id_raw_payload: null,
        patient_id_parser_version: null,
        patient_id_reviewed_by_user: false,
        insurance_carrier: null,
        insurance_policy: null,
        insurance_relationship: null,
        medical_history: [],
        current_medications: [],
        allergies: [],
        dispatch_reason: null,
        chief_complaint: null,
        primary_impression: null,
        secondary_impressions: [],
        signs: [],
        symptoms: [],
        scene_safety_flags: [],
        assessments: [],
        transport_decision: null,
        destination_hospital: null,
        hospital_choice_reason: null,
        loaded_mileage_miles: null,
        total_mileage_miles: null,
        mileage_source: null,
        mileage_gps_actual: null,
        mileage_mapped_route: null,
        mileage_manual_entry: null,
        mileage_confirmed_at: null,
        mileage_confirmed_by: null,
        mileage_override_reason: null,
        narrative_approved: false,
        locked: false,
        locked_by: null,
        locked_at: null,
        qa_signed: false,
        qa_signed_by: null,
        qa_signed_at: null,
        protocol_state: {
          triggered_protocol_ids: [],
          primary_protocol_id: null,
          related_protocol_ids: [],
          active_protocol_ids: [],
          decisions: [],
          completed_steps: [],
          deviations: [],
          last_evaluated_at: null,
        },
        transmission_status: 'not_submitted',
        transmission_attempts: 0,
        transmission_last_error: null,
        transmission_submitted_at: null,
        transmission_confirmed_at: null,
        compliance_fields: {},
        toc_completed: false,
        toc_receiving_facility: null,
        toc_completed_at: null,
        toc_delay_reason: null,
        toc_delivery_method: null,
        toc_confirmed_received: null,
        report_type: 'ems_primary',
        unit_id: null,
        unit_type: null,
        created_by: user!.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      await db.epcrs.add(withSyncMeta(newEpcr));
      await enqueueSync('epcrs', newEpcr.id, 'insert', newEpcr as unknown as Record<string, unknown>);
      setEpcr(newEpcr);
    }
    setLoading(false);
  }

  async function updateEpcr(updates: Partial<EPCR>) {
    if (!epcr) return;
    let patch: Partial<EPCR> = { ...updates, updated_at: new Date().toISOString() };

    // Recompute the RIDOH opioid-surveillance flag on every update so
    // the dataset stays accurate without a separate background job.
    // Skip the recomputation when the update itself is only touching
    // the flag to avoid a write loop.
    if (!('ridoh_opioid_surveillance' in updates)) {
      const simulated = { ...epcr, ...patch } as EPCR;
      const meds = await db.medications_given.where('epcr_id').equals(epcr.id).toArray();
      const { computeRidohSurveillance } = await import('@/lib/ridoh-surveillance');
      const { flagged } = computeRidohSurveillance(simulated, meds);
      if (flagged !== !!epcr.ridoh_opioid_surveillance) {
        patch = { ...patch, ridoh_opioid_surveillance: flagged };
      }
    }

    const updated = { ...epcr, ...patch };
    setEpcr(updated);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await db.epcrs.update(epcr.id, { ...patch, _dirty: true, _last_modified: new Date().toISOString() } as any);
    await enqueueSync('epcrs', epcr.id, 'update', updated as unknown as Record<string, unknown>);

    // Patient Registry upsert — when an ePCR transitions to locked
    // (the canonical "this is final" event), accumulate the patient
    // into the agency registry so future runs can recall demographics +
    // history + danger flags. Sprint K-Recall-2.
    if (updates.locked === true && !epcr.locked) {
      void import('@/lib/patient-registry').then(({ upsertPatientFromEpcr }) =>
        upsertPatientFromEpcr(updated),
      );
      void import('@/lib/epcr-audit').then(({ logEpcrAccess }) =>
        logEpcrAccess(epcr.id, 'lock'),
      );
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-slate-500" size={32} />
      </div>
    );
  }

  if (!epcr || !incident) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">ePCR not found</p>
        <button onClick={() => navigate(-1)} className="btn-secondary mt-4">Go Back</button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-screen-2xl mx-auto w-full">
      {/* Header — single tight row. Title on the left, compact icon-
          only action group on the right. Report type + unit + patient
          name live as small pills directly under the title so they
          stop wrapping into a narrow vertical column. The view-layout
          picker (RAIL / BOARD / COMMAND) was removed per Cameron's
          earlier rail-only directive. */}
      {/* Header laid out as up to three rows so nothing collides at
          tablet width:
            Row 1: back arrow · title · locked pill
            Row 2: report-type select · unit input · patient name
            Row 3: action group (Post Check / NEMSIS / TOC / Audit /
                   NERIS / Code 99)
          On wide displays (xl+) rows 2 and 3 sit side-by-side; below
          that they stack so the action buttons never overlap with
          the meta dropdowns. */}
      <div className="flex flex-col gap-2">
        {/* Row 1 — back + title + locked */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/incidents/${incidentId}`)}
            className="min-h-touch min-w-touch flex items-center justify-center rounded-lg hover:bg-slate-800 shrink-0"
            title="Back to incident"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-base sm:text-lg font-bold text-slate-100 truncate leading-tight flex-1 min-w-0">
            ePCR · {incident.run_number || 'No Run #'}
          </h1>
          {epcr.locked && (
            <span className="flex items-center gap-1 rounded-full bg-red-900 px-2.5 py-1 text-xs text-red-300 shrink-0">
              <Lock size={12} /> Locked
            </span>
          )}
        </div>

        {/* Rows 2 + 3 — meta + actions. xl: side-by-side; below xl:
            stacked. Both rows wrap their own children if their column
            ever gets too narrow. */}
        <div className="flex flex-col xl:flex-row xl:items-center gap-2 xl:gap-4">
          {/* Row 2 — meta */}
          <div className="flex items-center gap-1.5 text-xs text-slate-400 flex-wrap">
            <select
              value={epcr.report_type}
              onChange={(e) => updateEpcr({ report_type: e.target.value as EPCR['report_type'] })}
              disabled={epcr.locked}
              className="rounded bg-slate-800 border border-slate-700 px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-brand-400 min-w-[140px] shrink-0"
              title="Report type — EMS Primary, EMS Assist, Non-Transport, Interfacility, or Mutual Aid"
            >
              <option value="ems_primary">EMS Primary</option>
              <option value="ems_assist">EMS Assist</option>
              <option value="non_transport">Non-Transport</option>
              <option value="interfacility">Interfacility</option>
              <option value="mutual_aid">Mutual Aid</option>
            </select>
            <input
              type="text"
              value={epcr.unit_id ?? ''}
              onChange={(e) => updateEpcr({ unit_id: e.target.value || null })}
              disabled={epcr.locked}
              className="rounded bg-slate-800 border border-slate-700 px-2 py-1.5 text-xs text-slate-200 w-20 focus:outline-none focus:border-brand-400"
              placeholder="Unit"
            />
            <span className="text-slate-600">·</span>
            <span className="truncate min-w-0">
              {epcr.patient_last_name
                ? `${epcr.patient_last_name}, ${epcr.patient_first_name}`
                : 'No patient name entered'}
            </span>
          </div>

          {/* Row 3 — action group */}
          <div className="flex flex-wrap items-center gap-1 xl:ml-auto xl:justify-end">
            <IconAction
              onClick={() => setPostCheckOpen(true)}
              icon={<ShieldCheck size={16} />}
              label="Post Check"
              title="Run the Post-time validation check"
            />
            <IconAction
              onClick={() => setNemsisPreviewOpen(true)}
              icon={<FileText size={16} />}
              label="NEMSIS"
              title="Preview the NEMSIS v3.5.1 XML"
            />
            <IconAction
              onClick={() => navigate(`/incidents/${incidentId}/epcr/${epcr.id}/print`)}
              icon={<Printer size={16} />}
              label="TOC"
              title="Print Transfer of Care form"
            />
            <IconAction
              onClick={() => navigate(`/incidents/${incidentId}/epcr/${epcr.id}/audit`)}
              icon={<ClipboardCheck size={16} />}
              label="Audit"
              title="Compliance audit report (med director / QA)"
            />
            <NerisToggle epcr={epcr} incident={incident} />
            <Code99Button epcr={epcr} onOpen={() => setCode99Open(true)} />
          </div>
        </div>
      </div>

      {/* Workflow — layout is user-selectable via the header picker.
          Content is the same regardless of layout; only the chrome changes. */}
      {/* Import CAD / Import Vitals/ECG buttons relocated to the
          AppShell's diamond-plate banner so they're available from
          anywhere in the app, not just the ePCR scroll container.
          The banner is already sticky, and the EMT no longer has to
          jockey for vertical real estate at the top of the workflow. */}

      {/* Always-accessible run times — addendum § 9 + user request. */}
      <TimestampRail incidentId={incident.id} />

      {/* Conditional-set cascade banner — Foundation §3.3. Shows the
          Trauma / Cardiac Arrest / Stroke / etc. sets currently active
          on this ePCR, with tap-to-explain rule sources. Nothing
          renders when only the implicit base set is active. */}
      <ConditionalSetBanner epcr={epcr} />

      {/* Response delays moved into the Disposition tab — they're a
          dispositional fact (why a leg of the run took longer than
          expected), not a top-of-page concern competing with the
          run-times rail. */}

      {/* Single-column workflow body. The right-third protocol panel
          (ProtocolPanel + ProtocolReferenceDrawer iframe) was removed
          per Cameron's call: it was redundantly labeled, never finished
          its wiring, and was eating ~440px of horizontal space that
          forced every form field into a narrow center column. The
          protocol-driven reporting now lives where it belongs — inside
          the Procedures tab as the CareMatrix. */}
      <WorkflowBody
        layout={epcrLayout}
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k as TabKey)}
        getStatus={(k) => computeSectionStatus(k as TabKey, epcr)}
        content={
          <EpcrTabFrame
            tabKey={activeTab}
            sectionLabel={SECTIONS.find((s) => s.key === activeTab)?.label ?? ''}
            phase={SECTIONS.find((s) => s.key === activeTab)?.phase ?? ''}
            status={computeSectionStatus(activeTab as TabKey, epcr)}
          >
            {activeTab === 'patient' && <PatientDataTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'history' && <MedicalHistoryTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'dispatch' && <DispatchSceneTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'required' && <RequiredDocTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'assessment' && <AssessmentTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'vitals' && <VitalsTab epcr={epcr} />}
            {activeTab === 'procedures' && <ProceduresTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'medications' && <MedicationsTab epcr={epcr} />}
            {activeTab === 'disposition' && <DispositionTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'attachments' && <AttachmentsTab epcr={epcr} />}
            {activeTab === 'toc' && <TransferOfCareTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'summary' && <TreatmentSummaryTab epcr={epcr} />}
            {activeTab === 'narrative' && <NarrativeTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'billing' && <BillingTab epcr={epcr} onUpdate={updateEpcr} />}
            {activeTab === 'amendments' && <AmendmentsTab epcr={epcr} />}
          </EpcrTabFrame>
        }
      />

      {/* Code 99 resuscitation workspace — full-screen overlay when active */}
      {code99Open && incident && (
        <Code99Mode
          epcr={epcr}
          agencyId={incident.agency_id}
          incidentId={incident.id}
          onExit={() => setCode99Open(false)}
        />
      )}

      {/* Cascade removal prompt — Foundation §3.4. Watches the set
          deltas and asks Keep/Clear when a previously-active set drops.
          Default on dismiss = keep, so nothing is silently lost. */}
      <CascadeRemovalPrompt
        epcr={epcr}
        onClear={(_setId, patch) => updateEpcr(patch)}
      />

      {/* NEMSIS v3.5.1 XML preview — Foundation §6.3 export pipeline. */}
      {nemsisPreviewOpen && (
        <NemsisPreviewModal epcrId={epcr.id} onClose={() => setNemsisPreviewOpen(false)} />
      )}

      {/* Post-time validation modal — Foundation §9.2. */}
      {postCheckOpen && (
        <PostValidationModal
          epcr={epcr}
          onClose={() => setPostCheckOpen(false)}
          onUpdate={updateEpcr}
          onConfirmPost={() => {
            // Hand off to the existing transmission flow; the modal
            // is a preview gate, the TransmissionPill does the send.
            setPostCheckOpen(false);
          }}
          onJumpToTab={(k) => {
            setPostCheckOpen(false);
            setActiveTab(k as TabKey);
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Code 99 button — amber normally, pulsing red when the incident's
// impression or complaint suggests cardiac arrest (per addendum § 1.1).
// ------------------------------------------------------------------
/**
 * EpcrTabFrame — wraps the active tab's content in a cockpit-v2
 * SectionCard when the v2 theme is on, plain pass-through otherwise.
 *
 * This is the "one commit, every tab gets cockpit chrome" lever:
 * instead of editing each of the 16 tab files, we wrap their output
 * here. The kicker shows the workflow phase (Intake / Care / Handoff
 * / Admin), the title is the section label, and the status edge
 * threads the same hot/satisfied/blocker vocabulary as the rail —
 * single source of truth, two presentations.
 */
function EpcrTabFrame({
  tabKey, sectionLabel, phase, status, children,
}: {
  tabKey: string;
  sectionLabel: string;
  phase: string;
  status: SectionStatus;
  children: React.ReactNode;
}) {
  const cockpitV2 = useThemeStore((s) => s.cockpitV2);
  if (!cockpitV2) return <>{children}</>;
  const cardStatus: SectionCardStatus =
    status === 'complete' ? 'satisfied' :
    status === 'blocker'  ? 'blocked'   :
    'hot';                                // pending / partial both read as hot
  return (
    <SectionCard
      kicker={phase ? `${phase.toUpperCase()} · ${sectionLabel.toUpperCase()}` : sectionLabel.toUpperCase()}
      status={cardStatus}
      key={tabKey}    // remount on tab switch so the cooling state inside SectionCard resets cleanly
    >
      {children}
    </SectionCard>
  );
}

function Code99Button({ epcr, onOpen }: { epcr: EPCR; onOpen: () => void }) {
  const text = `${epcr.primary_impression ?? ''} ${epcr.chief_complaint ?? ''}`.toLowerCase();
  const suggested = /cardiac arrest|cpr in progress|pulseless|rosc/.test(text);
  return (
    <button
      onClick={onOpen}
      title="Open Code 99 resuscitation workspace"
      className={cn(
        // min-h-touch / min-w-touch matches the IconAction siblings in
        // the action group so this button visually aligns with the row.
        'min-h-touch min-w-touch flex items-center gap-1.5 px-3 text-xs font-bold uppercase tracking-wider border rounded transition-colors',
        suggested
          ? 'border-red-400 bg-red-600 text-white hover:bg-red-500 animate-pulse'
          : 'border-amber-500 bg-amber-600/20 text-amber-200 hover:bg-amber-600/40',
      )}
    >
      <HeartPulse size={16} />
      Code 99
    </button>
  );
}

// ------------------------------------------------------------------
// Layout-aware workflow renderer
// ------------------------------------------------------------------
function WorkflowBody({
  layout, activeKey, onSelect, getStatus, content,
}: {
  layout: EpcrLayoutMode;
  activeKey: string;
  onSelect: (k: string) => void;
  getStatus: (k: string) => SectionStatus;
  content: React.ReactNode;
}) {
  const common = { sections: SECTIONS, activeKey, onSelect, getStatus, content };
  if (layout === 'grid')    return <GridLauncherWorkflow {...common} />;
  if (layout === 'command') return <CommandRailWorkflow  {...common} />;
  return <LeftRailWorkflow {...common} />;
}

// ------------------------------------------------------------------
// IconAction — icon + text-label button. The label was previously
// hidden behind a tooltip-only affordance, but icon-only menus are
// hostile when half the chiefs are reading from a desktop without
// hovering everything. Now the label is inline. Title attribute
// stays for screen-reader / extra-context hover.
// ------------------------------------------------------------------
function IconAction({
  onClick, icon, label, title,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title ?? label}
      aria-label={label}
      className="min-h-touch flex items-center gap-1.5 px-3 rounded text-xs font-bold uppercase tracking-wider border border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-slate-100 hover:border-slate-500"
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

/**
 * NerisToggle — top-bar affordance that exposes NERIS readiness from
 * inside the PCR. Renders hot-steel red while any NERIS-required
 * field is unfilled and cools to base metal when every required
 * field is satisfied. Click jumps to the linked fire report (or to
 * the incident page if no fire report exists yet).
 *
 * Uses the same computeNerisReadiness() the FireReportPage pill
 * uses so both surfaces stay in sync — captured-once pipeline.
 */
function NerisToggle({ epcr, incident }: { epcr: EPCR; incident: Incident | null }) {
  const navigate = useNavigate();
  const [fireReport, setFireReport] = useState<FireReport | null>(null);

  // Load the linked fire report (if any) so the readiness
  // calculation has all three sources. Re-runs when the underlying
  // ePCR or incident changes; fire-report fields don't change here
  // so the load happens once on mount.
  useEffect(() => {
    if (!incident) return;
    let cancel = false;
    void (async () => {
      const reports = await db.fire_reports.where('incident_id').equals(incident.id).toArray();
      if (!cancel) setFireReport(reports[0] ?? null);
    })();
    return () => { cancel = true; };
  }, [incident?.id]);

  if (!incident) return null;

  const readiness = computeNerisReadiness(incident, epcr, fireReport);
  const ready = readiness.filled === readiness.total;

  return (
    <button
      type="button"
      onClick={() => {
        if (fireReport) {
          navigate(`/incidents/${incident.id}/fire`);
        } else {
          navigate(`/incidents/${incident.id}`);
        }
      }}
      title={ready
        ? 'NERIS — all required fields satisfied. Tap to view the fire report.'
        : `NERIS — ${readiness.filled}/${readiness.total} required fields filled (${readiness.fromPcr}/${readiness.totalFromPcr} from this PCR). Tap to open the fire report.`}
      className={cn(
        'min-h-touch flex items-center gap-1.5 px-3 rounded text-xs font-bold uppercase tracking-wider border transition-colors',
        ready
          ? 'border-emerald-700 bg-emerald-900/40 text-emerald-200 hover:bg-emerald-900/60'
          : 'rail-button rail-button-hot-blocker',
      )}
    >
      <Flame size={16} />
      <span>NERIS</span>
      <span className="font-mono text-[10px] opacity-90">
        {readiness.filled}/{readiness.total}
      </span>
    </button>
  );
}

